import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Badge,
  Box,
  Flex,
  SimpleGrid,
  useToast,
  IconButton,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon, StarIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { getAuth } from 'firebase/auth';
import { Category } from '../../types/game.ts';
import './CategorySelector.css';

interface CategorySelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoriesSelected: (selectedCategories: Category[]) => void;
  initialSelectedCategories?: Category[];
}

const MotionBox = motion(Box);

const CategorySelector: React.FC<CategorySelectorProps> = ({
  isOpen,
  onClose,
  onCategoriesSelected,
  initialSelectedCategories = [],
}) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  
  // États
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [favoriteCategories, setFavoriteCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>(initialSelectedCategories);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Debounce la recherche pour éviter trop de re-calculs
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Calculer les catégories filtrées avec useMemo pour éviter les re-renders en boucle
  const filteredCategories = useMemo(() => {
    console.log('🔄 [CategorySelector] Recalcul du filtrage, recherche:', debouncedSearchQuery, 'sélectionnées:', selectedCategories.length);
    console.log('🔄 [CategorySelector] État: allCategories:', allCategories.length, 'favoriteCategories:', favoriteCategories.length, 'activeTab:', activeTab);
    
    // Utiliser les favoris ou toutes les catégories selon l'onglet actif
    // Pour les utilisateurs authentifiés non anonymes: onglet 0 = toutes, onglet 1 = favoris
    // Pour les utilisateurs anonymes: onglet 0 = toutes seulement
    const currentUser = getAuth().currentUser;
    const isAuthenticatedUser = currentUser && !currentUser.isAnonymous;
    const sourceCategories = (isAuthenticatedUser && activeTab === 1) ? favoriteCategories : allCategories;
    
    console.log('🔄 [CategorySelector] Source categories:', sourceCategories.length, 'from', isAuthenticatedUser && activeTab === 1 ? 'favoris' : 'toutes');
    
    let filtered = sourceCategories.filter(category => 
      !selectedCategories.some(selected => selected.id === category.id)
    );
    
    if (debouncedSearchQuery.trim() !== '') {
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        (category.creator_name && category.creator_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()))
      );
    }
    
    console.log('📋 [CategorySelector] Catégories filtrées résultantes:', filtered.length);
    return filtered;
  }, [allCategories, favoriteCategories, selectedCategories, debouncedSearchQuery, activeTab]);

  // Synchroniser avec les catégories initiales SEULEMENT au premier rendu et quand la modal s'ouvre
  useEffect(() => {
    if (isOpen && initialSelectedCategories.length > 0 && selectedCategories.length === 0) {
      console.log('🔄 [CategorySelector] Initialisation avec les catégories fournies:', initialSelectedCategories.map(c => c.name));
      setSelectedCategories(initialSelectedCategories);
    }
  }, [isOpen]);

  // Debounce la recherche pour éviter trop de re-calculs
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Synchroniser avec les catégories initiales SEULEMENT au premier rendu et quand la modal s'ouvre
  useEffect(() => {
    if (isOpen && initialSelectedCategories.length > 0 && selectedCategories.length === 0) {
      console.log('🔄 [CategorySelector] Initialisation avec les catégories fournies:', initialSelectedCategories.map(c => c.name));
      setSelectedCategories(initialSelectedCategories);
    }
  }, [isOpen]);

  // Charger toutes les catégories disponibles et réinitialiser l'état
  useEffect(() => {
    if (isOpen) {
      // Réinitialiser l'état de recherche à chaque ouverture
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setError(null);
      setActiveTab(0); // Reset to first tab
      // Force rechargement des catégories depuis le serveur (pas de cache)
      fetchAllCategories(true);
      // Charger les favoris seulement si l'utilisateur est authentifié et non anonyme
      const currentUser = getAuth().currentUser;
      if (currentUser && !currentUser.isAnonymous) {
        fetchFavoriteCategories();
      } else {
        console.log('🔐 [CategorySelector] Utilisateur anonyme ou non connecté, pas de chargement des favoris');
        setFavoriteCategories([]);
      }
    } else {
      // Nettoyer complètement l'état quand la modal se ferme
      console.log('🧹 [CategorySelector] Nettoyage de l\'état lors de la fermeture');
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setError(null);
      setIsLoading(false);
      setIsLoadingFavorites(false);
      setActiveTab(0);
      // Ne pas réinitialiser selectedCategories ici pour garder la sélection
    }
  }, [isOpen, i18n.language]);

  const fetchAllCategories = async (forceRefresh = false) => {
    console.log('🔍 [CategorySelector] fetchAllCategories - Début', { forceRefresh, currentUser: getAuth().currentUser });
    setIsLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      console.log('🔍 [CategorySelector] API_URL:', API_URL);
      
      // Ajouter un timestamp pour éviter le cache si forceRefresh est true
      const url = forceRefresh 
        ? `${API_URL}/api/categories/${i18n.language}?_t=${Date.now()}`
        : `${API_URL}/api/categories/${i18n.language}`;
      
      console.log('🔍 [CategorySelector] Fetching URL:', url);
      
      // Configuration simplifiée mais explicite pour tous les navigateurs
      const fetchOptions = {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        mode: 'cors' as RequestMode,
        credentials: 'omit' as RequestCredentials,
        cache: forceRefresh ? 'no-cache' as RequestCache : 'default' as RequestCache
      };
      
      console.log('🔍 [CategorySelector] Fetch options:', fetchOptions);
      const response = await fetch(url, fetchOptions);
      
      console.log('🔍 [CategorySelector] Response status:', response.status, response.statusText);
      console.log('🔍 [CategorySelector] Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 [CategorySelector] Response error text:', errorText);
        
        // Si la requête échoue, essayons avec une URL sans paramètres de langue
        if (response.status === 404 || response.status === 0) {
          console.log('🔍 [CategorySelector] Tentative avec URL de fallback...');
          const fallbackUrl = `${API_URL}/api/categories`;
          const fallbackResponse = await fetch(fallbackUrl, fetchOptions);
          
          if (fallbackResponse.ok) {
            const responseText = await fallbackResponse.text();
            console.log('🔍 [CategorySelector] Fallback raw response:', responseText);
            
            let categories;
            try {
              categories = JSON.parse(responseText);
              console.log('📦 [CategorySelector] Catégories chargées (fallback):', categories.length, categories);
              setAllCategories(categories);
              
              // Sélectionner automatiquement les catégories par défaut si aucune n'est sélectionnée
              if (selectedCategories.length === 0) {
                const defaultCategories = categories.filter((cat: Category) => !cat.is_custom && !cat.is_mystery);
                console.log('📦 [CategorySelector] Catégories par défaut sélectionnées:', defaultCategories);
                setSelectedCategories(defaultCategories);
              }
              return;
            } catch (parseError) {
              console.error('🔍 [CategorySelector] Fallback JSON parse error:', parseError);
            }
          }
        }
        
        throw new Error('Erreur lors du chargement des catégories');
      }
      
      const responseText = await response.text();
      console.log('🔍 [CategorySelector] Raw response:', responseText);
      
      let categories;
      try {
        categories = JSON.parse(responseText);
      } catch (parseError) {
        console.error('🔍 [CategorySelector] JSON parse error:', parseError);
        throw new Error('Réponse invalide du serveur');
      }
      console.log('📦 [CategorySelector] Catégories chargées:', categories.length, categories);
      
      setAllCategories(categories);
      
      // Si c'est la première ouverture et qu'aucune catégorie n'est sélectionnée,
      // ET qu'on n'a pas d'initialSelectedCategories
      if (selectedCategories.length === 0 && initialSelectedCategories.length === 0) {
        const defaultCategories = categories.filter((cat: Category) => !cat.is_custom || cat.is_mystery);
        console.log('📦 [CategorySelector] Catégories par défaut sélectionnées:', defaultCategories.map(c => c.name));
        setSelectedCategories(defaultCategories);
      }
      
    } catch (err) {
      console.error('❌ [CategorySelector] Erreur:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les catégories',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
      console.log('🔍 [CategorySelector] fetchAllCategories - Fin');
    }
  };

  const fetchFavoriteCategories = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    // Ne pas essayer de charger les favoris pour les utilisateurs anonymes
    if (!user || (user.isAnonymous && user.providerData.length === 0)) {
      console.log('🔐 [CategorySelector] Utilisateur anonyme, pas de favoris');
      setFavoriteCategories([]);
      return;
    }

    setIsLoadingFavorites(true);
    
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const token = await user.getIdToken();
      
      const response = await fetch(`${API_URL}/api/user/favorites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': i18n.language
        }
      });
      
      if (!response.ok) {
        // Ne pas considérer comme une erreur critique pour les favoris
        console.warn('⚠️ [CategorySelector] Impossible de charger les favoris:', response.status);
        setFavoriteCategories([]);
        return;
      }
      
      const result = await response.json();
      console.log('⭐ [CategorySelector] Favoris chargés:', result);
      
      // Extraire la liste des favoris depuis la réponse
      const favoritesData = result.favorites || result || [];
      setFavoriteCategories(favoritesData);
      
    } catch (err) {
      console.warn('⚠️ [CategorySelector] Erreur lors du chargement des favoris:', err);
      // Ne pas afficher d'erreur pour les favoris, c'est optionnel
      setFavoriteCategories([]);
    } finally {
      setIsLoadingFavorites(false);
    }
  };

  const handleCategoryToggle = (category: Category) => {
    console.log('🔄 [CategorySelector] Toggle catégorie:', category.name);
    
    setSelectedCategories(prevSelected => {
      const isSelected = prevSelected.some(cat => cat.id === category.id);
      
      if (isSelected) {
        console.log('➖ [CategorySelector] Retrait de:', category.name);
        return prevSelected.filter(cat => cat.id !== category.id);
      } else {
        console.log('➕ [CategorySelector] Ajout de:', category.name);
        return [...prevSelected, category];
      }
    });
  };

  const handleRemoveCategory = (categoryId: string) => {
    console.log('🗑️ [CategorySelector] Suppression de la catégorie:', categoryId);
    
    try {
      // Utiliser une fonction callback pour éviter les problèmes de fermeture
      setSelectedCategories(prevSelected => {
        const categoryToRemove = prevSelected.find(cat => cat.id === categoryId);
        console.log('🗑️ [CategorySelector] Catégorie à supprimer:', categoryToRemove?.name);
        
        const updated = prevSelected.filter(cat => cat.id !== categoryId);
        console.log('📦 [CategorySelector] Catégories restantes après suppression:', updated.map(c => c.name));
        
        // Validation des catégories restantes
        const validatedCategories = updated.filter(cat => cat && cat.id && cat.name);
        if (validatedCategories.length !== updated.length) {
          console.warn('⚠️ [CategorySelector] Catégories corrompues détectées et supprimées');
        }
        
        return validatedCategories;
      });
      
    } catch (error) {
      console.error('❌ [CategorySelector] Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Problème lors de la suppression de la catégorie',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Fonction pour nettoyer et valider les catégories
  const cleanCategories = (categories: Category[]): Category[] => {
    return categories.map(cat => ({
      id: cat.id,
      name: cat.name,
      color: cat.color || 'blue.400',
      emoji: cat.emoji,
      is_mystery: cat.is_mystery || false,
      is_custom: cat.is_custom || false,
      creator_name: cat.creator_name || undefined,
      created_at: cat.created_at || new Date().toISOString()
    })).filter(cat => cat.id && cat.name);
  };

  const handleConfirm = () => {
    console.log('🔄 [CategorySelector] handleConfirm appelé avec', selectedCategories.length, 'catégories');
    
    if (selectedCategories.length === 0) {
      toast({
        title: 'Attention',
        description: 'Vous devez sélectionner au moins une catégorie',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    
    try {
      console.log('✅ [CategorySelector] Confirmation avec catégories:', selectedCategories.map(c => c.name));
      console.log('📋 [CategorySelector] Détails des catégories confirmées:', selectedCategories.map(c => ({ 
        id: c.id, 
        name: c.name, 
        is_custom: c.is_custom, 
        is_mystery: c.is_mystery 
      })));
      
      // Nettoyer les catégories avant de les envoyer
      const cleanedCategories = cleanCategories(selectedCategories);
      console.log('🧹 [CategorySelector] Catégories nettoyées:', cleanedCategories.length);
      
      // FORCER la fermeture de la modal immédiatement
      console.log('🚪 [CategorySelector] Fermeture forcée de la modal...');
      handleModalClose();
      
      // Envoyer les catégories avec un délai pour éviter les conflits
      setTimeout(() => {
        console.log('📤 [CategorySelector] Envoi des catégories vers GameSetup');
        onCategoriesSelected(cleanedCategories);
      }, 100);
      
    } catch (error) {
      console.error('❌ [CategorySelector] Erreur dans handleConfirm:', error);
      // Même en cas d'erreur, fermer la modal
      handleModalClose();
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la confirmation',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleSelectAll = () => {
    if (activeTab === 1) {
      // Si on est sur l'onglet favoris, sélectionner tous les favoris
      setSelectedCategories(prev => {
        const newCategories = favoriteCategories.filter(fav => 
          !prev.some(selected => selected.id === fav.id)
        );
        return [...prev, ...newCategories];
      });
    } else {
      setSelectedCategories([...allCategories]);
    }
  };

  const handleSelectDefaults = () => {
    const defaultCategories = allCategories.filter(cat => !cat.is_custom || cat.is_mystery);
    setSelectedCategories(defaultCategories);
  };

  const handleSelectAllFavorites = () => {
    setSelectedCategories(prev => {
      const newCategories = favoriteCategories.filter(fav => 
        !prev.some(selected => selected.id === fav.id)
      );
      return [...prev, ...newCategories];
    });
  };

  const handleClearAll = () => {
    setSelectedCategories([]);
  };

  // Fonction de nettoyage complète
  const handleModalClose = () => {
    console.log('🚪 [CategorySelector] Modal fermée - nettoyage complet');
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setError(null);
    setIsLoading(false);
    setIsLoadingFavorites(false);
    setActiveTab(0);
    onClose();
  };

  // Ne pas rendre le contenu si la modal n'est pas ouverte
  if (!isOpen) {
    return null;
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleModalClose} 
      size="6xl" 
      scrollBehavior="inside"
      onCloseComplete={() => {
        console.log('🧹 [CategorySelector] Modal complètement fermée');
      }}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent 
        className="category-selector-modal"
        maxH={{ base: "90vh", md: "85vh", lg: "90vh" }}
        mx={{ base: 2, md: 1, lg: 4 }}
        my={{ base: 2, md: 1, lg: 4 }}
        maxW={{ base: "95vw", md: "98vw", lg: "95vw", xl: "7xl" }}
        w={{ base: "95vw", md: "98vw", lg: "95vw", xl: "auto" }}
        h={{ base: "auto", md: "85vh", lg: "auto" }}
        display="flex"
        flexDirection="column"
      >
        {/* Header simplifié avec fermeture et compteur */}
        <ModalHeader className="category-selector-header" py={3}>
          <HStack justify="space-between" align="center" w="full">
            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="600" className="category-selector-text">
              Sélection des Catégories
            </Text>
            <Badge 
              colorScheme={selectedCategories.length > 0 ? "blue" : "gray"} 
              fontSize={{ base: "sm", md: "md" }} 
              px={3} 
              py={1}
              borderRadius="full"
            >
              {selectedCategories.length} sélectionnée{selectedCategories.length > 1 ? 's' : ''}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton className="category-selector-close-button" />
        
        <ModalBody className="category-selector-body" py={2} pb={8}>
          {/* Interface unifiée - Une seule liste avec états visuels */}
          <VStack spacing={3} h="full" pb={4}>

            {/* Onglets pour filtrer */}
            <Tabs 
              index={activeTab} 
              onChange={setActiveTab}
              variant="soft-rounded"
              colorScheme="blue"
              size="sm"
              w="full"
            >
              <TabList justifyContent="center" mb={2}>
                <Tab fontSize={{ base: "sm", md: "md" }} px={3} py={2}>
                  Toutes ({allCategories.length})
                </Tab>
                {getAuth().currentUser && !getAuth().currentUser?.isAnonymous && (
                  <Tab fontSize={{ base: "sm", md: "md" }} px={3} py={2}>
                    ⭐ Favoris ({favoriteCategories.length})
                  </Tab>
                )}
              </TabList>

              <TabPanels flex={1} display="flex" flexDirection="column">
                {/* Onglet: Toutes les catégories */}
                <TabPanel p={0} h="full" display="flex" flexDirection="column">
                  <VStack spacing={2} h="full">
                    {/* Barre de recherche */}
                    <InputGroup size={{ base: "md", md: "lg" }}>
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon className="category-selector-muted-text" />
                      </InputLeftElement>
                      <Input
                        placeholder={t('categorySelector.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="category-selector-input"
                        fontSize={{ base: "md", md: "lg" }}
                      />
                    </InputGroup>

                    {/* Boutons d'action rapide - Mobile/Tablet */}
                    <HStack spacing={1} w="full" justify="center" display={{ base: "flex", md: "none" }}>
                      <Button 
                        size="xs"
                        onClick={handleSelectDefaults} 
                        variant="outline"
                        className="category-selector-button select-defaults"
                        leftIcon={<StarIcon boxSize="2.5" />}
                        px={2}
                      >
                        Défaut
                      </Button>
                      <Button 
                        size="xs"
                        onClick={handleSelectAll} 
                        variant="outline"
                        className="category-selector-button select-all"
                        leftIcon={<span style={{fontSize: '0.875rem'}}>✓</span>}
                        px={2}
                      >
                        Tout
                      </Button>
                      <Button 
                        size="xs"
                        onClick={handleClearAll} 
                        variant="outline"
                        className="category-selector-button clear-all"
                        leftIcon={<CloseIcon boxSize="2.5" />}
                        px={2}
                        isDisabled={selectedCategories.length === 0}
                      >
                        Vider
                      </Button>
                    </HStack>

                    {/* Boutons d'action - Écrans moyens (laptop/tablet large) */}
                    <HStack spacing={2} w="full" justify="center" display={{ base: "none", md: "flex", xl: "none" }}>
                      <Button 
                        size="sm"
                        onClick={handleSelectDefaults} 
                        variant="outline"
                        className="category-selector-button select-defaults"
                        leftIcon={<StarIcon boxSize="3" />}
                        px={4}
                      >
                        Par défaut
                      </Button>
                      <Button 
                        size="sm"
                        onClick={handleSelectAll} 
                        variant="outline"
                        className="category-selector-button select-all"
                        leftIcon={<span style={{fontSize: '1rem'}}>✓</span>}
                        px={4}
                      >
                        Tout sélectionner
                      </Button>
                      <Button 
                        size="sm"
                        onClick={handleClearAll} 
                        variant="outline"
                        className="category-selector-button clear-all"
                        leftIcon={<CloseIcon boxSize="3" />}
                        px={4}
                        isDisabled={selectedCategories.length === 0}
                      >
                        Tout effacer
                      </Button>
                    </HStack>

                    {/* Boutons d'action pour desktop - version élargie */}
                    <HStack spacing={3} w="full" justify="center" display={{ base: "none", xl: "flex" }}>
                      <Button 
                        size="md"
                        onClick={handleSelectDefaults} 
                        variant="outline"
                        className="category-selector-button select-defaults"
                        leftIcon={<StarIcon boxSize="4" />}
                        px={6}
                      >
                        Sélection par défaut
                      </Button>
                      <Button 
                        size="md"
                        onClick={handleSelectAll} 
                        variant="outline"
                        className="category-selector-button select-all"
                        leftIcon={<span style={{fontSize: '1.25rem'}}>✓</span>}
                        px={6}
                      >
                        Tout sélectionner
                      </Button>
                      <Button 
                        size="md"
                        onClick={handleClearAll} 
                        variant="outline"
                        className="category-selector-button clear-all"
                        leftIcon={<CloseIcon boxSize="4" />}
                        px={6}
                        isDisabled={selectedCategories.length === 0}
                      >
                        Tout effacer
                      </Button>
                    </HStack>

                    {/* Liste unifiée des catégories */}
                    <Box 
                      flex={1} 
                      w="full"
                      overflowY="auto" 
                      maxH={{ 
                        base: "calc(90vh - 280px)", 
                        md: "calc(85vh - 300px)", 
                        lg: "calc(90vh - 280px)" 
                      }}
                      borderRadius="lg" 
                      p={{ base: 3, md: 4 }}
                      pb={{ base: 8, md: 10 }} // Padding encore plus important en bas
                      className="category-selector-scrollable"
                      border="1px solid"
                      borderColor="var(--chakra-colors-gray-700, #2D3748)"
                      bg="var(--chakra-colors-gray-800, #1A202C)"
                      css={{
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'var(--chakra-colors-gray-700)',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'var(--chakra-colors-gray-500)',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: 'var(--chakra-colors-gray-400)',
                        },
                      }}
                    >
                      {isLoading ? (
                        <Flex justify="center" align="center" h="200px">
                          <VStack spacing={3}>
                            <Spinner size="lg" color="var(--bleu-electrique)" />
                            <Text className="category-selector-muted-text">Chargement...</Text>
                          </VStack>
                        </Flex>
                      ) : error ? (
                        <Alert status="error" borderRadius="md">
                          <AlertIcon color="red.500" />
                          <Text className="category-selector-text">{error}</Text>
                        </Alert>
                      ) : (
                        <Box>
                          {/* Version Desktop - Grille de cartes */}
                          <Box display={{ base: "none", xl: "block" }}>
                            <SimpleGrid 
                              columns={{ xl: 2, "2xl": 3 }} 
                              spacing={4}
                              w="full"
                            >
                              {(activeTab === 0 ? allCategories : favoriteCategories)
                                .filter(category => {
                                  if (debouncedSearchQuery.trim() === '') return true;
                                  return category.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                                         (category.creator_name && category.creator_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
                                })
                                .map((category) => {
                                  const isSelected = selectedCategories.some(cat => cat.id === category.id);
                                  
                                  return (
                                    <MotionBox
                                      key={category.id}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      <Box
                                        p={4}
                                        className={`category-selector-card ${isSelected ? 'selected' : ''}`}
                                        borderRadius="lg"
                                        cursor="pointer"
                                        onClick={() => handleCategoryToggle(category)}
                                        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                        position="relative"
                                        border="2px solid"
                                        borderColor={isSelected ? "var(--bleu-electrique)" : "transparent"}
                                        bg={isSelected ? "rgba(0, 168, 255, 0.1)" : "var(--chakra-colors-gray-700, #2D3748)"}
                                        _hover={{
                                          borderColor: isSelected ? "var(--bleu-electrique)" : "var(--chakra-colors-gray-600, #4A5568)",
                                          bg: isSelected ? "rgba(0, 168, 255, 0.15)" : "var(--chakra-colors-gray-600, #4A5568)"
                                        }}
                                        _before={category.color ? {
                                          content: '""',
                                          position: 'absolute',
                                          left: 0,
                                          top: 0,
                                          bottom: 0,
                                          width: '4px',
                                          bg: category.color,
                                          borderTopLeftRadius: 'lg',
                                          borderBottomLeftRadius: 'lg'
                                        } : undefined}
                                        minH="120px"
                                      >
                                        <VStack spacing={3} h="full" justify="space-between">
                                          {/* Header avec emoji et nom */}
                                          <VStack spacing={2} w="full">
                                            <Text fontSize="2xl">
                                              {category.emoji || (category.is_custom ? '👤' : '📝')}
                                            </Text>
                                            <Text 
                                              fontWeight="600" 
                                              fontSize="md"
                                              textAlign="center"
                                              className="category-selector-text"
                                              noOfLines={2}
                                              lineHeight="1.3"
                                            >
                                              {category.name}
                                            </Text>
                                          </VStack>
                                          
                                          {/* Badges et créateur */}
                                          <VStack spacing={2} w="full">
                                            <HStack spacing={1} justify="center" flexWrap="wrap">
                                              {category.is_custom ? (
                                                <Badge colorScheme="purple" size="xs" borderRadius="full">
                                                  Custom
                                                </Badge>
                                              ) : (
                                                <Badge colorScheme="blue" size="xs" borderRadius="full">
                                                  Officielle
                                                </Badge>
                                              )}
                                              
                                              {category.is_mystery && (
                                                <Badge colorScheme="red" size="xs" borderRadius="full">
                                                  🔮
                                                </Badge>
                                              )}
                                              
                                              {activeTab === 1 && (
                                                <Badge colorScheme="yellow" size="xs" borderRadius="full">
                                                  ⭐
                                                </Badge>
                                              )}
                                            </HStack>
                                            
                                            {category.creator_name && (
                                              <Text 
                                                fontSize="xs" 
                                                className="category-selector-muted-text"
                                                textAlign="center"
                                                noOfLines={1}
                                              >
                                                Par {category.creator_name}
                                              </Text>
                                            )}
                                          </VStack>
                                          
                                          {/* Indicateur de sélection */}
                                          <Box
                                            position="absolute"
                                            top={2}
                                            right={2}
                                            w={6}
                                            h={6}
                                            borderRadius="full"
                                            border="2px solid"
                                            borderColor={isSelected ? "var(--bleu-electrique)" : "var(--chakra-colors-gray-500, #718096)"}
                                            bg={isSelected ? "var(--bleu-electrique)" : "transparent"}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            transition="all 0.2s ease"
                                          >
                                            {isSelected && (
                                              <Text color="white" fontWeight="bold" fontSize="xs">
                                                ✓
                                              </Text>
                                            )}
                                          </Box>
                                        </VStack>
                                      </Box>
                                    </MotionBox>
                                  );
                                })}
                            </SimpleGrid>
                            
                            {/* Espace pour éviter que le footer masque les dernières catégories */}
                            <Box h={{ base: "120px", md: "120px", lg: "120px", xl: "120px"  }} />
                          </Box>

                          {/* Version Mobile/Tablet/Laptop - Liste verticale */}
                          <VStack spacing={2} align="stretch" display={{ base: "flex", xl: "none" }}>
                            {(activeTab === 0 ? allCategories : favoriteCategories)
                              .filter(category => {
                                if (debouncedSearchQuery.trim() === '') return true;
                                return category.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                                       (category.creator_name && category.creator_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
                              })
                              .map((category) => {
                                const isSelected = selectedCategories.some(cat => cat.id === category.id);
                                
                                return (
                                  <MotionBox
                                    key={category.id}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                  <Box
                                    p={{ base: 4, md: 5 }}
                                    className={`category-selector-card ${isSelected ? 'selected' : ''}`}
                                    borderRadius="lg"
                                    cursor="pointer"
                                    onClick={() => handleCategoryToggle(category)}
                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                    position="relative"
                                    border="2px solid"
                                    borderColor={isSelected ? "var(--bleu-electrique)" : "transparent"}
                                    bg={isSelected ? "rgba(0, 168, 255, 0.1)" : "var(--chakra-colors-gray-700, #2D3748)"}
                                    _hover={{
                                      borderColor: isSelected ? "var(--bleu-electrique)" : "var(--chakra-colors-gray-600, #4A5568)",
                                      bg: isSelected ? "rgba(0, 168, 255, 0.15)" : "var(--chakra-colors-gray-600, #4A5568)"
                                    }}
                                    _before={category.color ? {
                                      content: '""',
                                      position: 'absolute',
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: '5px',
                                      bg: category.color,
                                      borderTopLeftRadius: 'lg',
                                      borderBottomLeftRadius: 'lg'
                                    } : undefined}
                                  >
                                    <HStack justify="space-between" align="center" spacing={4}>
                                      <VStack align="start" spacing={2} flex={1} minW={0}>
                                        <HStack spacing={3} w="full" align="center">
                                          {/* Emoji de la catégorie */}
                                          <Text fontSize={{ base: "lg", md: "xl" }}>
                                            {category.emoji || (category.is_custom ? '👤' : '📝')}
                                          </Text>
                                          
                                          <VStack align="start" spacing={1} flex={1} minW={0}>
                                            <Text 
                                              fontWeight="600" 
                                              fontSize={{ base: "md", md: "lg" }}
                                              lineHeight="1.2"
                                              className="category-selector-text"
                                              noOfLines={1}
                                            >
                                              {category.name}
                                            </Text>
                                            
                                            <HStack spacing={2} flexWrap="wrap">
                                              {category.is_custom ? (
                                                <Badge colorScheme="purple" size="sm" fontSize="xs" borderRadius="full">
                                                  Personnalisée
                                                </Badge>
                                              ) : (
                                                <Badge colorScheme="blue" size="sm" fontSize="xs" borderRadius="full">
                                                  Officielle
                                                </Badge>
                                              )}
                                              
                                              {category.is_mystery && (
                                                <Badge colorScheme="red" size="sm" fontSize="xs" borderRadius="full">
                                                  🔮 Mystère
                                                </Badge>
                                              )}
                                              
                                              {activeTab === 1 && (
                                                <Badge colorScheme="yellow" size="sm" fontSize="xs" borderRadius="full">
                                                  ⭐ Favori
                                                </Badge>
                                              )}
                                            </HStack>
                                          </VStack>
                                        </HStack>
                                        
                                        {category.creator_name && (
                                          <Text 
                                            fontSize="xs" 
                                            className="category-selector-muted-text"
                                            pl={8}
                                            noOfLines={1}
                                          >
                                            Par {category.creator_name}
                                          </Text>
                                        )}
                                      </VStack>
                                      
                                      {/* Indicateur de sélection */}
                                      <Box
                                        w={8}
                                        h={8}
                                        borderRadius="full"
                                        border="2px solid"
                                        borderColor={isSelected ? "var(--bleu-electrique)" : "var(--chakra-colors-gray-500, #718096)"}
                                        bg={isSelected ? "var(--bleu-electrique)" : "transparent"}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        transition="all 0.2s ease"
                                      >
                                        {isSelected && (
                                          <Text color="white" fontWeight="bold" fontSize="sm">
                                            ✓
                                          </Text>
                                        )}
                                      </Box>
                                    </HStack>
                                  </Box>
                                </MotionBox>
                              );
                            })}
                          </VStack>

                          {/* Espace pour éviter que le footer masque les dernières catégories */}
                          <Box h={{ base: "120px", md: "100px" }} />
                          
                          {/* Message si aucune catégorie - Version unifiée */}
                          {(activeTab === 0 ? allCategories : favoriteCategories)
                            .filter(category => {
                              if (debouncedSearchQuery.trim() === '') return true;
                              return category.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                                     (category.creator_name && category.creator_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
                            }).length === 0 && (
                            <Flex justify="center" align="center" h="200px">
                              <VStack spacing={3}>
                                <Text fontSize="2xl">🔍</Text>
                                <Text textAlign="center" className="category-selector-muted-text">
                                  {debouncedSearchQuery ? 'Aucune catégorie trouvée' : (activeTab === 1 ? 'Aucun favori' : 'Aucune catégorie')}
                                </Text>
                              </VStack>
                            </Flex>
                          )}
                        </Box>
                      )}
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Onglet: Catégories favorites */}
                {getAuth().currentUser && !getAuth().currentUser?.isAnonymous && (
                  <TabPanel p={0} h="full" display="flex" flexDirection="column">
                    {/* Même contenu que l'onglet précédent mais avec favoriteCategories */}
                    <VStack spacing={3} h="full">
                      {/* Barre de recherche */}
                      <InputGroup size={{ base: "md", md: "lg" }}>
                        <InputLeftElement pointerEvents="none">
                          <SearchIcon className="category-selector-muted-text" />
                        </InputLeftElement>
                        <Input
                          placeholder={t('categorySelector.searchPlaceholder')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="category-selector-input"
                          fontSize={{ base: "md", md: "lg" }}
                        />
                      </InputGroup>

                      {/* Boutons d'action pour les favoris - Version mobile */}
                      <HStack spacing={2} w="full" justify="center" display={{ base: "flex", md: "none" }}>
                        <Button 
                          size="sm"
                          onClick={handleSelectAllFavorites} 
                          variant="outline"
                          className="category-selector-button select-all"
                          leftIcon={<StarIcon boxSize="3" />}
                          isDisabled={favoriteCategories.length === 0}
                        >
                          Tous les favoris
                        </Button>
                        <Button 
                          size="sm"
                          onClick={handleClearAll} 
                          variant="outline"
                          className="category-selector-button clear-all"
                          leftIcon={<CloseIcon boxSize="3" />}
                          isDisabled={selectedCategories.length === 0}
                        >
                          Vider
                        </Button>
                      </HStack>

                      {/* Boutons d'action pour les favoris - Écrans moyens */}
                      <HStack spacing={2} w="full" justify="center" display={{ base: "none", md: "flex", xl: "none" }}>
                        <Button 
                          size="md"
                          onClick={handleSelectAllFavorites} 
                          variant="outline"
                          className="category-selector-button select-all"
                          leftIcon={<StarIcon boxSize="3" />}
                          isDisabled={favoriteCategories.length === 0}
                          px={4}
                        >
                          Tous les favoris
                        </Button>
                        <Button 
                          size="md"
                          onClick={handleClearAll} 
                          variant="outline"
                          className="category-selector-button clear-all"
                          leftIcon={<CloseIcon boxSize="3" />}
                          isDisabled={selectedCategories.length === 0}
                          px={4}
                        >
                          Tout effacer
                        </Button>
                      </HStack>

                      {/* Boutons d'action pour les favoris - Version desktop */}
                      <HStack spacing={3} w="full" justify="center" display={{ base: "none", xl: "flex" }}>
                        <Button 
                          size="md"
                          onClick={handleSelectAllFavorites} 
                          variant="outline"
                          className="category-selector-button select-all"
                          leftIcon={<StarIcon boxSize="4" />}
                          isDisabled={favoriteCategories.length === 0}
                          px={6}
                        >
                          Sélectionner tous les favoris
                        </Button>
                        <Button 
                          size="md"
                          onClick={handleClearAll} 
                          variant="outline"
                          className="category-selector-button clear-all"
                          leftIcon={<CloseIcon boxSize="4" />}
                          isDisabled={selectedCategories.length === 0}
                          px={6}
                        >
                          Tout effacer
                        </Button>
                      </HStack>

                      {/* Liste des favoris - même structure que ci-dessus */}
                      <Box 
                        flex={1} 
                        w="full"
                        overflowY="auto" 
                        maxH={{ base: "55vh", md: "80vh", lg: "70vh" }}
                        borderRadius="lg" 
                        p={{ base: 3, md: 4 }}
                        className="category-selector-scrollable"
                        border="1px solid"
                        borderColor="var(--chakra-colors-gray-700, #2D3748)"
                        bg="var(--chakra-colors-gray-800, #1A202C)"
                        css={{
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'var(--chakra-colors-gray-700)',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: 'var(--chakra-colors-gray-500)',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb:hover': {
                            background: 'var(--chakra-colors-gray-400)',
                          },
                        }}
                      >
                        {isLoadingFavorites ? (
                          <Flex justify="center" align="center" h="200px">
                            <VStack spacing={3}>
                              <Spinner size="lg" color="var(--bleu-electrique)" />
                              <Text className="category-selector-muted-text">Chargement des favoris...</Text>
                            </VStack>
                          </Flex>
                        ) : favoriteCategories.length === 0 ? (
                          <Flex justify="center" align="center" h="200px">
                            <VStack spacing={3}>
                              <Text fontSize="3xl">⭐</Text>
                              <Text textAlign="center" className="category-selector-muted-text">
                                Aucune catégorie favorite
                              </Text>
                              <Text textAlign="center" fontSize="sm" className="category-selector-muted-text">
                                Ajoutez des catégories à vos favoris depuis la liste principale
                              </Text>
                            </VStack>
                          </Flex>
                        ) : (
                          <Box>
                            {/* Version Desktop - Grille de cartes pour favoris */}
                            <Box display={{ base: "none", xl: "block" }}>
                              <SimpleGrid 
                                columns={{ xl: 2, "2xl": 3 }} 
                                spacing={4}
                                w="full"
                              >
                                {favoriteCategories
                                  .filter(category => {
                                    if (debouncedSearchQuery.trim() === '') return true;
                                    return category.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                                           (category.creator_name && category.creator_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
                                  })
                                  .map((category) => {
                                    const isSelected = selectedCategories.some(cat => cat.id === category.id);
                                    
                                    return (
                                      <MotionBox
                                        key={category.id}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <Box
                                          p={4}
                                          className={`category-selector-card ${isSelected ? 'selected' : ''}`}
                                          borderRadius="lg"
                                          cursor="pointer"
                                          onClick={() => handleCategoryToggle(category)}
                                          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                          position="relative"
                                          border="2px solid"
                                          borderColor={isSelected ? "var(--bleu-electrique)" : "transparent"}
                                          bg={isSelected ? "rgba(0, 168, 255, 0.1)" : "var(--chakra-colors-gray-700, #2D3748)"}
                                          _hover={{
                                            borderColor: isSelected ? "var(--bleu-electrique)" : "var(--chakra-colors-gray-600, #4A5568)",
                                            bg: isSelected ? "rgba(0, 168, 255, 0.15)" : "var(--chakra-colors-gray-600, #4A5568)"
                                          }}
                                          _before={category.color ? {
                                            content: '""',
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: '4px',
                                            bg: category.color,
                                            borderTopLeftRadius: 'lg',
                                            borderBottomLeftRadius: 'lg'
                                          } : undefined}
                                          minH="120px"
                                        >
                                          <VStack spacing={3} h="full" justify="space-between">
                                            {/* Header avec emoji et nom */}
                                            <VStack spacing={2} w="full">
                                              <Text fontSize="2xl">
                                                {category.emoji || (category.is_custom ? '👤' : '📝')}
                                              </Text>
                                              <Text 
                                                fontWeight="600" 
                                                fontSize="md"
                                                textAlign="center"
                                                className="category-selector-text"
                                                noOfLines={2}
                                                lineHeight="1.3"
                                              >
                                                {category.name}
                                              </Text>
                                            </VStack>
                                            
                                            {/* Badges et créateur */}
                                            <VStack spacing={2} w="full">
                                              <HStack spacing={1} justify="center" flexWrap="wrap">
                                                {category.is_custom ? (
                                                  <Badge colorScheme="purple" size="xs" borderRadius="full">
                                                    Custom
                                                  </Badge>
                                                ) : (
                                                  <Badge colorScheme="blue" size="xs" borderRadius="full">
                                                    Officielle
                                                  </Badge>
                                                )}
                                                
                                                {category.is_mystery && (
                                                  <Badge colorScheme="red" size="xs" borderRadius="full">
                                                    🔮
                                                  </Badge>
                                                )}
                                                
                                                <Badge colorScheme="yellow" size="xs" borderRadius="full">
                                                  ⭐
                                                </Badge>
                                              </HStack>
                                              
                                              {category.creator_name && (
                                                <Text 
                                                  fontSize="xs" 
                                                  className="category-selector-muted-text"
                                                  textAlign="center"
                                                  noOfLines={1}
                                                >
                                                  Par {category.creator_name}
                                                </Text>
                                              )}
                                            </VStack>
                                            
                                            {/* Indicateur de sélection */}
                                            <Box
                                              position="absolute"
                                              top={2}
                                              right={2}
                                              w={6}
                                              h={6}
                                              borderRadius="full"
                                              border="2px solid"
                                              borderColor={isSelected ? "var(--bleu-electrique)" : "var(--chakra-colors-gray-500, #718096)"}
                                              bg={isSelected ? "var(--bleu-electrique)" : "transparent"}
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              transition="all 0.2s ease"
                                            >
                                              {isSelected && (
                                                <Text color="white" fontWeight="bold" fontSize="xs">
                                                  ✓
                                                </Text>
                                              )}
                                            </Box>
                                          </VStack>
                                        </Box>
                                      </MotionBox>
                                    );
                                  })}
                              </SimpleGrid>
                            </Box>

                            {/* Version Mobile/Tablet/Laptop - Liste verticale pour favoris */}
                            <VStack spacing={2} align="stretch" display={{ base: "flex", xl: "none" }}>
                              {favoriteCategories
                                .filter(category => {
                                  if (debouncedSearchQuery.trim() === '') return true;
                                  return category.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                                         (category.creator_name && category.creator_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
                                })
                                .map((category) => {
                                  const isSelected = selectedCategories.some(cat => cat.id === category.id);
                                  
                                  return (
                                    <MotionBox
                                      key={category.id}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ duration: 0.2 }}
                                    >
                                    <Box
                                      p={{ base: 4, md: 5 }}
                                      className={`category-selector-card ${isSelected ? 'selected' : ''}`}
                                      borderRadius="lg"
                                      cursor="pointer"
                                      onClick={() => handleCategoryToggle(category)}
                                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                      position="relative"
                                      border="2px solid"
                                      borderColor={isSelected ? "var(--bleu-electrique)" : "transparent"}
                                      bg={isSelected ? "rgba(0, 168, 255, 0.1)" : "var(--chakra-colors-gray-700, #2D3748)"}
                                      _hover={{
                                        borderColor: isSelected ? "var(--bleu-electrique)" : "var(--chakra-colors-gray-600, #4A5568)",
                                        bg: isSelected ? "rgba(0, 168, 255, 0.15)" : "var(--chakra-colors-gray-600, #4A5568)"
                                      }}
                                      _before={category.color ? {
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: '5px',
                                        bg: category.color,
                                        borderTopLeftRadius: 'lg',
                                        borderBottomLeftRadius: 'lg'
                                      } : undefined}
                                    >
                                      <HStack justify="space-between" align="center" spacing={4}>
                                        <VStack align="start" spacing={2} flex={1} minW={0}>
                                          <HStack spacing={3} w="full" align="center">
                                            {/* Emoji de la catégorie */}
                                            <Text fontSize={{ base: "lg", md: "xl" }}>
                                              {category.emoji || (category.is_custom ? '👤' : '📝')}
                                            </Text>
                                            
                                            <VStack align="start" spacing={1} flex={1} minW={0}>
                                              <Text 
                                                fontWeight="600" 
                                                fontSize={{ base: "md", md: "lg" }}
                                                lineHeight="1.2"
                                                className="category-selector-text"
                                                noOfLines={1}
                                              >
                                                {category.name}
                                              </Text>
                                              
                                              <HStack spacing={2} flexWrap="wrap">
                                                {category.is_custom ? (
                                                  <Badge colorScheme="purple" size="sm" fontSize="xs" borderRadius="full">
                                                    Personnalisée
                                                  </Badge>
                                                ) : (
                                                  <Badge colorScheme="blue" size="sm" fontSize="xs" borderRadius="full">
                                                    Officielle
                                                  </Badge>
                                                )}
                                                
                                                {category.is_mystery && (
                                                  <Badge colorScheme="red" size="sm" fontSize="xs" borderRadius="full">
                                                    🔮 Mystère
                                                  </Badge>
                                                )}
                                                
                                                <Badge colorScheme="yellow" size="sm" fontSize="xs" borderRadius="full">
                                                  ⭐ Favori
                                                </Badge>
                                              </HStack>
                                            </VStack>
                                          </HStack>
                                          
                                          {category.creator_name && (
                                            <Text 
                                              fontSize="xs" 
                                              className="category-selector-muted-text"
                                              pl={8}
                                              noOfLines={1}
                                            >
                                              Par {category.creator_name}
                                            </Text>
                                          )}
                                        </VStack>
                                        
                                        {/* Indicateur de sélection */}
                                        <Box
                                          w={8}
                                          h={8}
                                          borderRadius="full"
                                          border="2px solid"
                                          borderColor={isSelected ? "var(--bleu-electrique)" : "var(--chakra-colors-gray-500, #718096)"}
                                          bg={isSelected ? "var(--bleu-electrique)" : "transparent"}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          transition="all 0.2s ease"
                                        >
                                          {isSelected && (
                                            <Text color="white" fontWeight="bold" fontSize="sm">
                                              ✓
                                            </Text>
                                          )}
                                        </Box>
                                      </HStack>
                                    </Box>
                                  </MotionBox>
                                );
                              })}
                            </VStack>
                            
                            {/* Espace pour éviter que le footer masque les dernières catégories favoris */}
                            <Box h={{ base: "120px", md: "100px" }} />
                            
                            {/* Message si aucune catégorie favorite filtrée */}
                            {favoriteCategories
                              .filter(category => {
                                if (debouncedSearchQuery.trim() === '') return true;
                                return category.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                                       (category.creator_name && category.creator_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
                              }).length === 0 && debouncedSearchQuery.trim() !== '' && (
                              <Flex justify="center" align="center" h="200px">
                                <VStack spacing={3}>
                                  <Text fontSize="2xl">🔍</Text>
                                  <Text textAlign="center" className="category-selector-muted-text">
                                    Aucun favori trouvé pour "{debouncedSearchQuery}"
                                  </Text>
                                </VStack>
                              </Flex>
                            )}
                          </Box>
                        )}
                      </Box>
                    </VStack>
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </VStack>
        </ModalBody>

        <ModalFooter className="category-selector-footer" py={4} pt={6}>
          <HStack spacing={2} w="full" justify={{ base: "stretch", md: "flex-end" }}>
            <Button 
              variant="ghost" 
              onClick={onClose}
              className="category-selector-button"
              flex={{ base: 1, md: "none" }}
              size="md"
            >
              Annuler
            </Button>
            <Button
              bg="#00A8FF"
              color="white"
              _hover={{ bg: '#0090dd' }}
              _active={{ bg: '#0077b3' }}
              onClick={handleConfirm}
              isDisabled={selectedCategories.length === 0}
              flex={{ base: 1, md: "none" }}
              size="md"
              boxShadow="0 0 0 2px rgba(0,168,255,0.15)"
            >
              Confirmer ({selectedCategories.length})
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CategorySelector;
